import React, { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Box } from '@mui/material';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// Hoist currency formatter to avoid recreation on every render
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Read-only grid showing Model After (reference) accounts with base period data
 */
export default function ModelAfterGrid({ accounts, months }) {
  // Column definitions
  const columnDefs = useMemo(() => {
    const cols = [
      {
        headerName: 'Account Name',
        field: 'account_name',
        pinned: 'left',
        width: 250,
        cellStyle: { fontWeight: 'bold' },
      },
      {
        headerName: 'Base Period Total',
        field: 'basePeriodTotal',
        width: 150,
        valueFormatter: params => {
          if (params.value === null || params.value === undefined) return '';
          return currencyFormatter.format(params.value);
        },
      },
    ];

    // Add columns for each month
    months.forEach(month => {
      cols.push({
        headerName: month,
        field: `month_${month}`,
        width: 120,
        valueFormatter: params => {
          if (params.value === null || params.value === undefined) return '';
          return currencyFormatter.format(params.value);
        },
      });
    });

    return cols;
  }, [months]);

  // Row data
  const rowData = useMemo(() => {
    return accounts.map((account, index) => {
      const row = {
        glKey: account.glKey, // Add glKey for stable row identity
        account_name: account.account_name,
        basePeriodTotal: account.basePeriodTotal,
      };

      // Add monthly values - dates should already be in 'MMM yyyy' format
      if (account.dates) {
        Object.entries(account.dates).forEach(([date, value]) => {
          const monthKey = `month_${date}`;
          row[monthKey] = Number(value) || 0;
        });
      }
      
      // Ensure all months have a value (0 if not present)
      months.forEach(month => {
        const monthKey = `month_${month}`;
        if (!(monthKey in row)) {
          row[monthKey] = 0;
        }
      });

      return row;
    });
  }, [accounts, months]);

  return (
    <Box className="ag-theme-alpine" sx={{ height: 400, width: '100%' }}>
      <AgGridReact
        columnDefs={columnDefs}
        rowData={rowData}
        domLayout="normal"
        suppressMovableColumns={true}
        getRowId={(params) => params.data.glKey}
        defaultColDef={{
          sortable: true,
          resizable: true,
        }}
      />
    </Box>
  );
}
